import mammoth from "mammoth";
import { quitarDiacriticos } from "./texto";

export const SECCIONES_ASSESSMENT = [
  { key: "referencia", titulo: "Referencia del Cliente" },
  { key: "objetivo", titulo: "Objetivo del Estudio de", tipo: "lista" },
  { key: "cronograma", titulo: "Cronograma a Alto Nivel" },
  { key: "bom", titulo: "BOM", columnas: 3 },
  { key: "entrega", titulo: "Entrega y Aceptación" },
];

const MAX_LOGOS = 2;

const CAMPOS_REFERENCIA = {
  "fecha": "fecha",
  "servicio": "servicio",
  "ticket": "servicio",
  "nombre / razon social": "nombreRazonSocial",
  "telefono": "telefono",
  "direccion": "direccion",
  "contacto": "contacto",
  "puesto": "puestoContacto",
  "puesto contacto": "puestoContacto",
  "puesto del contacto": "puestoContacto",
  "cargo": "puestoContacto",
  "proyecto": "proyecto",
  "oportunidad": "proyecto",
};

const FILAS_CRONOGRAMA = 7;
const COLUMNAS_CRONOGRAMA = 7;

const REGEX_NUMERACION = /^\s*\d+\.\s*/;
const REGEX_CORREO = /[^\s]+@[^\s]+\.[^\s]+/;
const REGEX_TIPO_REPORTE = /^reporte de\s+(.+)$/i;
const TIPO_REPORTE_DEFAULT = "Site Survey";

const normalizar = (texto) =>
  quitarDiacriticos(texto.replace(REGEX_NUMERACION, ""))
    .toLowerCase()
    .trim();

export const base64ADatos = (base64) => {
  const binario = atob(base64);
  const bytes = new Uint8Array(binario.length);
  for (let i = 0; i < binario.length; i += 1) {
    bytes[i] = binario.charCodeAt(i);
  }
  return bytes;
};

const buscarSeccion = (texto) => {
  const normalizado = normalizar(texto);
  return SECCIONES_ASSESSMENT.find((s) => normalizado.includes(normalizar(s.titulo)));
};

const textoCelda = (celda) => {
  const parrafos = Array.from(celda.querySelectorAll("p"))
    .map((p) => p.textContent.trim())
    .filter(Boolean);
  return parrafos.length > 0 ? parrafos.join("\n") : celda.textContent.trim();
};

const extraerFilasTabla = (tabla, columnas) =>
  Array.from(tabla.querySelectorAll("tr"))
    .map((fila) => Array.from(fila.querySelectorAll("td,th")).map(textoCelda))
    .filter((fila) => fila.some((celda) => celda.length > 0))
    .map((fila) => {
      const ajustada = fila.slice(0, columnas);
      while (ajustada.length < columnas) ajustada.push("");
      return ajustada;
    });

const filaAObjetoColumnas = (fila) =>
  fila.reduce((acc, valor, indice) => ({ ...acc, [`col${indice + 1}`]: valor }), {});

const procesarListaRecursiva = (ul, profundidad, items) => {
  Array.from(ul.children).forEach((li) => {
    if (li.tagName.toLowerCase() !== "li") return;
    const textoPropio = Array.from(li.childNodes)
      .filter((n) => !(n.nodeType === 1 && ["ul", "ol"].includes(n.tagName.toLowerCase())))
      .map((n) => n.textContent)
      .join("")
      .trim();
    if (textoPropio) {
      items.push({ texto: (profundidad > 0 ? "   ○ " : "• ") + textoPropio });
    }
    Array.from(li.children)
      .filter((c) => ["ul", "ol"].includes(c.tagName.toLowerCase()))
      .forEach((subLista) => procesarListaRecursiva(subLista, profundidad + 1, items));
  });
};

const extraerCamposReferencia = (tabla) => {
  const campos = {};
  Array.from(tabla.querySelectorAll("tr")).forEach((fila) => {
    const celdas = Array.from(fila.querySelectorAll("td,th")).map(textoCelda);
    for (let i = 0; i < celdas.length - 1; i += 2) {
      const campo = CAMPOS_REFERENCIA[normalizar(celdas[i])];
      if (campo) campos[campo] = celdas[i + 1] || "";
    }
  });
  return campos;
};

const extraerMarcasCronograma = (tabla) => {
  const marcas = {};
  for (let f = 1; f <= FILAS_CRONOGRAMA; f += 1) {
    for (let s = 1; s <= COLUMNAS_CRONOGRAMA; s += 1) {
      marcas[`cronP${f}S${s}`] = "";
    }
  }
  const filas = Array.from(tabla.querySelectorAll("tr")).slice(1, FILAS_CRONOGRAMA + 1);
  filas.forEach((fila, indiceFila) => {
    const celdas = Array.from(fila.querySelectorAll("td,th"))
      .map(textoCelda)
      .slice(1, COLUMNAS_CRONOGRAMA + 1);
    celdas.forEach((valor, indiceColumna) => {
      marcas[`cronP${indiceFila + 1}S${indiceColumna + 1}`] = valor;
    });
  });
  return marcas;
};

const extraerFilasBom = (tabla) =>
  extraerFilasTabla(tabla, 3).filter((fila) => {
    const primera = normalizar(fila[0] || "");
    return primera !== "bom" && primera !== "no parte";
  });

const extraerCorreoIngeniero = (tabla) => {
  const texto = Array.from(tabla.querySelectorAll("td,th")).map(textoCelda).join("\n");
  const coincidencia = texto.match(REGEX_CORREO);
  return coincidencia ? coincidencia[0] : "";
};

const extraerTipoReporte = (documento) => {
  const primerH1 = documento.body.querySelector("h1");
  const nodos = Array.from(documento.body.childNodes).filter((n) => n.nodeType === 1);
  const nodosPortada = primerH1 ? nodos.slice(0, nodos.indexOf(primerH1)) : nodos;

  for (const nodo of nodosPortada) {
    const texto = (nodo.textContent || "").trim();
    const coincidencia = texto.match(REGEX_TIPO_REPORTE);
    if (coincidencia) return coincidencia[1].trim();
  }
  return TIPO_REPORTE_DEFAULT;
};

const extraerSeccionesDesdeHtml = (html, logos) => {
  const documento = new DOMParser().parseFromString(html, "text/html");
  const nodos = Array.from(documento.body.childNodes).filter((n) => n.nodeType === 1);
  const tipoReporte = extraerTipoReporte(documento);

  const objetivoItems = [];
  let camposReferencia = {};
  let marcasCronograma = {};
  let filasBom = [];
  let imagenBom = null;
  let correoIngeniero = "";
  let bomIntro = "";

  let seccionActual = null;
  let tablaEncontrada = false;
  let indiceImagenActual = 0;
  const seccionesEncontradas = new Set();

  nodos.forEach((nodo) => {
    const etiqueta = nodo.tagName ? nodo.tagName.toLowerCase() : "";

    if (nodo.querySelector && nodo.querySelector("img")) {
      if (seccionActual === "bom" && !imagenBom) {
        imagenBom = logos[indiceImagenActual] || null;
      }
      indiceImagenActual += 1;
    }

    if (etiqueta === "h1") {
      const encontrada = buscarSeccion((nodo.textContent || "").trim());
      seccionActual = encontrada ? encontrada.key : null;
      if (encontrada) seccionesEncontradas.add(encontrada.key);
      tablaEncontrada = false;
      return;
    }

    if (!seccionActual) return;

    if (seccionActual === "objetivo") {
      if (etiqueta === "ul" || etiqueta === "ol") {
        procesarListaRecursiva(nodo, 0, objetivoItems);
      }
      return;
    }

    if (seccionActual === "bom" && etiqueta === "p" && !tablaEncontrada && !bomIntro) {
      const texto = (nodo.textContent || "").trim();
      if (texto) bomIntro = texto;
      return;
    }

    if (etiqueta === "table") {
      if (!tablaEncontrada) {
        if (seccionActual === "referencia") camposReferencia = extraerCamposReferencia(nodo);
        if (seccionActual === "cronograma") marcasCronograma = extraerMarcasCronograma(nodo);
        if (seccionActual === "bom") filasBom = extraerFilasBom(nodo);
        if (seccionActual === "entrega") correoIngeniero = extraerCorreoIngeniero(nodo);
        tablaEncontrada = true;
      }
    }
  });

  return {
    ...camposReferencia,
    objetivoItems,
    ...marcasCronograma,
    bomFilas: filasBom.map(filaAObjetoColumnas),
    bomIntro,
    imagenBom,
    correoIngeniero,
    tipoReporte,
    tieneReferencia: seccionesEncontradas.has("referencia"),
    tieneObjetivo: seccionesEncontradas.has("objetivo"),
    tieneCronograma: seccionesEncontradas.has("cronograma"),
    tieneBom: seccionesEncontradas.has("bom"),
    tieneEntrega: seccionesEncontradas.has("entrega"),
  };
};

export const extraerDatosAssessment = async (archivo) => {
  const arrayBuffer = await archivo.arrayBuffer();
  const logos = [];

  const { value: html } = await mammoth.convertToHtml(
    { arrayBuffer },
    {
      convertImage: mammoth.images.imgElement((imagen) =>
        imagen.read("base64").then((base64) => {
          logos.push({ base64, contentType: imagen.contentType });
          return { src: "" };
        })
      ),
    }
  );

  const secciones = extraerSeccionesDesdeHtml(html, logos);
  const logosTop = logos.slice(0, MAX_LOGOS);
  const logoCliente = logosTop.length > 1 ? logosTop[1] : logosTop[0] || null;

  return { ...secciones, logoCliente };
};